import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Plus } from "lucide-react";
import { api } from "./api";

const FIELD = "w-full rounded-xl bg-white border border-border px-4 py-2.5 outline-none focus:ring-2 focus:ring-coral focus:border-transparent";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ title: "", due_date: "", priority: "normal", notes: "" });
  const load = useCallback(async () => {
    const { data } = await api().get("/admin/tasks");
    setTasks(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required.");
    await api().post("/admin/tasks", form);
    setForm({ title: "", due_date: "", priority: "normal", notes: "" });
    load(); toast.success("Task created.");
  };
  const complete = async (t) => { await api().patch(`/admin/tasks/${t.id}`, { status: "done" }); load(); };
  const remove = async (t) => { await api().delete(`/admin/tasks/${t.id}`); load(); };

  const today = new Date().toISOString().slice(0, 10);
  const open = tasks.filter(t => t.status !== "done");
  const done = tasks.filter(t => t.status === "done");

  return (
    <div className="space-y-5" data-testid="admin-tasks-page">
      <form onSubmit={create} data-testid="new-task-form" className="rounded-2xl bg-white border border-border p-5 grid md:grid-cols-5 gap-3">
        <input data-testid="task-title" placeholder="Task title" className={`${FIELD} md:col-span-2`} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        <input data-testid="task-due" type="date" className={FIELD} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
        <select data-testid="task-priority" className={FIELD} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
          <option value="normal">Normal</option><option value="high">High</option>
        </select>
        <button data-testid="task-submit" type="submit" className="rounded-full bg-coral text-cream font-semibold px-4 py-2.5 inline-flex items-center justify-center gap-1"><Plus size={14} /> Add task</button>
      </form>

      <section>
        <h3 className="font-poppins font-bold text-lg mb-3">Open ({open.length})</h3>
        <div className="space-y-2">
          {open.map(t => {
            const overdue = t.due_date && t.due_date <= today;
            return (
              <div key={t.id} data-testid={`task-${t.id}`} className={`rounded-2xl bg-white border p-4 flex flex-wrap items-center gap-3 ${overdue ? "border-coral" : "border-border"}`}>
                <button onClick={() => complete(t)} data-testid={`task-complete-${t.id}`} className="h-6 w-6 rounded-full border-2 border-charcoal/30 hover:border-coral" title="Mark done" />
                <div className="flex-1">
                  <p className="font-semibold text-charcoal">{t.title}</p>
                  <p className="text-xs text-charcoal/60">Due {t.due_date || "—"} · {t.priority} · {t.source} {t.lead_id ? <Link to={`/admin/leads/${t.lead_id}`} className="text-coral">· view lead</Link> : null}</p>
                </div>
                {overdue && <span className="text-xs font-bold text-coral uppercase tracking-wider">Overdue</span>}
                <button onClick={() => remove(t)} className="text-xs text-charcoal/50 hover:text-charcoal">Delete</button>
              </div>
            );
          })}
          {!open.length && <p className="text-charcoal/50 text-sm">No open tasks. Nice.</p>}
        </div>
      </section>

      {!!done.length && (
        <section>
          <h3 className="font-poppins font-bold text-lg mb-3 text-charcoal/60">Completed ({done.length})</h3>
          <div className="space-y-2">
            {done.slice(0, 20).map(t => (
              <div key={t.id} className="rounded-2xl bg-white/60 border border-border p-3 flex items-center gap-3 opacity-70">
                <CheckCircle2 size={16} className="text-coral" />
                <p className="text-sm line-through text-charcoal/70">{t.title}</p>
                <p className="ml-auto text-xs text-charcoal/40">{t.done_at?.slice(0, 10)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
